import { ref, watch, type Ref } from 'vue'
import { online } from './useOnline'

// Module-level stale-while-revalidate cache shared across component instances.
// A cached entry within ttl paints immediately (loading=false) and still triggers
// a background revalidate; older/absent entries block on the first fetch. Mirrors
// the load-once/module-state pattern in useFavorites / useAuth.
const store = new Map<string, { data: unknown; ts: number }>()

/**
 * Smid én noegle vaek, saa naeste useCached henter forfra i stedet for at male en foraeldet
 * vaerdi imens den revaliderer. Bruges efter en mutation: admin retter et sted -> baade
 * admin-listen OG den offentlige 'sites'/'site:<id>' skal glemmes, ellers kan de to sider
 * vise hver sin sandhed i samme fane indtil TTL'en loeber ud.
 */
export function invalidate(...keys: string[]): void {
  for (const key of keys) store.delete(key)
}

/** Som invalidate, men for alt der starter med et praefiks (fx alle 'admin:'-noegler). */
export function invalidateAll(prefix: string): void {
  for (const key of [...store.keys()]) if (key.startsWith(prefix)) store.delete(key)
}

/** Toemmer hele cachen. Kaldes ved logout — se lib/offlineStorage. */
export function clearCache(): void {
  store.clear()
}

export function useCached<T>(key: string, fetcher: () => Promise<T>, ttlMs = 300000) {
  const data = ref<T | null>(null) as Ref<T | null>
  const loading = ref(true)
  const error = ref('')

  async function refresh(): Promise<void> {
    try {
      const fresh = await fetcher()
      data.value = fresh
      store.set(key, { data: fresh, ts: Date.now() })
      error.value = ''
    } catch (e) {
      // Keep any existing (stale) data on failure.
      error.value = e instanceof Error ? e.message : String(e)
    } finally {
      loading.value = false
    }
  }

  const cached = store.get(key)
  if (cached && Date.now() - cached.ts < ttlMs) {
    data.value = cached.data as T
    loading.value = false
  } else {
    loading.value = true
  }
  // Always revalidate in the background so displayed data stays fresh. Offline this
  // still resolves from the service-worker cache (NetworkFirst falls back to cache).
  void refresh()

  // When connectivity returns, revalidate so the offline snapshot updates. Registered in
  // the caller's effect scope (useCached runs in setup), so it's disposed on unmount.
  watch(online, (isOnline, was) => {
    if (isOnline && !was) void refresh()
  })

  return { data, loading, error, refresh }
}
